import React, { useCallback, useMemo } from 'react';

import {
  LocationNameDisplay,
  useLocations,
} from '@adsk/acc-locations-components';
import HighlightText from '@adsk/alloy-react-highlight-text';
import { useLocale } from '@adsk/alloy-react-locale';
import {
  actions as tableActions,
  Table,
  LoadMoreDataRow,
  SkeletonLoadingVertebra,
  OnAction,
  Column,
  CellProps,
} from '@adsk/alloy-react-table-v2';
import Theme from '@adsk/alloy-react-theme';
import Tooltip, { OverflowTooltip } from '@adsk/alloy-react-tooltip';

import CategoryName from './CategoryName';
import LoadingErrorCell from './LoadingErrorCell';
import StatusCell from './StatusCell';
import { Asset } from './types';
import ReferencePickerPlugin from '../../types/ReferencePickerPlugin';

const defaultColumn = {
  minWidth: 80,
  /* Initial width, but will grow proportionally within container */
  width: 80,
} as const;
const getRowId = (asset: Asset) => asset.id;
const mapifyArray = (arr: string[]) =>
  arr.reduce<Record<string, true>>((map, item) => {
    map[item] = true;
    return map;
  }, {});

const renderCategoryCell = ({ row }: CellProps<Asset>) => (
  <OverflowTooltip>
    {!row.original.category ? (
      <LoadingErrorCell />
    ) : (
      <CategoryName category={row.original.category} />
    )}
  </OverflowTooltip>
);

const renderStatusCell = ({ row }: CellProps<Asset>) => (
  <OverflowTooltip variant={Tooltip.VARIANTS.DARK}>
    {!row.original.status ? (
      <LoadingErrorCell />
    ) : (
      <StatusCell disabled={!!row.state.isDisabled} asset={row.original} />
    )}
  </OverflowTooltip>
);

const tableStyle = { height: '100%' };

const AssetsCustomDataView: Exclude<
  ReferencePickerPlugin<Asset>['CustomDataView'],
  undefined
> = ({
  disabledIds = [],
  selectedIds = [],
  entities: assets = [],
  onLoadMore,
  onChange,
  multiple: isMultiSelectable,
  error,
  filters: { searchExpression },
  setSelection,
  onTryAgain,
}) => {
  const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
  const { isLoading: isLoadingLocations } = useLocations();

  const columns: Column<Asset>[] = useMemo(() => {
    // Note: by naming Cell components we avoid unnecessary rerenders
    // some components, which depend on props in the parent component are defined here,
    // while components which have no external dependencies are defined outside of this
    // component.
    const renderAssetCell = ({ value }: CellProps<Asset>) => (
      <OverflowTooltip variant={Tooltip.VARIANTS.DARK}>
        <HighlightText matched={searchExpression} text={value} />
      </OverflowTooltip>
    );

    const renderDescriptionCell = ({ value }: CellProps<Asset>) => (
      <OverflowTooltip variant={Tooltip.VARIANTS.DARK}>
        <HighlightText matched={searchExpression} text={value} />
      </OverflowTooltip>
    );

    const renderLocationCell = ({ value, row }: CellProps<Asset>) =>
      isLoadingLocations ? (
        <SkeletonLoadingVertebra />
      ) : (
        <OverflowTooltip variant={Tooltip.VARIANTS.DARK}>
          {value && (
            <LocationNameDisplay
              style={
                row.state.isDisabled
                  ? { color: Theme.colors.charcoal500 }
                  : undefined
              }
              locationNodeId={value}
              notFoundText=""
            />
          )}
        </OverflowTooltip>
      );

    return [
      {
        accessor: 'clientAssetId',
        renderHeader: () => T.translate('ASSET_NAME'),
        renderCell: renderAssetCell,
        width: 250,
      },
      {
        accessor: 'categoryId',
        renderHeader: () => T.translate('CATEGORY'),
        renderCell: renderCategoryCell,
        width: 200,
      },
      {
        accessor: 'statusId',
        renderHeader: () => T.translate('STATUS'),
        renderCell: renderStatusCell,
        width: 200,
      },
      {
        accessor: 'description',
        renderHeader: () => T.translate('DESCRIPTION'),
        renderCell: renderDescriptionCell,
        width: 200,
      },
      {
        accessor: 'locationId',
        renderHeader: () => T.translate('LOCATION'),
        renderCell: renderLocationCell,
        width: 200,
      },
    ];
  }, [T, searchExpression, isLoadingLocations]);

  const disabledRowIds = useMemo(() => mapifyArray(disabledIds), [disabledIds]);
  const selectedRowIds = useMemo(() => mapifyArray(selectedIds), [selectedIds]);

  const onAction = useCallback<OnAction<Asset>>(
    ({ action }) => {
      const checked = Boolean(action.value);

      if (action.type === tableActions.toggleRowSelected) {
        const entity = assets.find((asset) => asset.id === action.id);
        if (entity) onChange({ entity, checked });
      } else if (action.type === tableActions.toggleAllRowsSelected) {
        if (checked) {
          setSelection(
            assets.filter(
              ({ id }) => !disabledRowIds[id] && !selectedRowIds[id],
            ),
            true, // keepExisting. Preserve selection between searches.
          );
        } else {
          setSelection([]);
        }
      }
    },
    [assets, onChange, setSelection, disabledRowIds, selectedRowIds],
  );

  const renderLastRow = useCallback(() => {
    const onLoad = onLoadMore
      ? async () => onLoadMore()
      : error
        ? async () => onTryAgain()
        : undefined;

    return !onLoad ? null : (
      <LoadMoreDataRow
        // Commented out the isAuto because it caused duplicate network requests.
        // Will bring it back after updating the table.
        // isAuto
        hasError={!!error}
        onRetry={async () => onTryAgain()}
        onLoad={onLoad}
        style={{}}
      />
    );
  }, [onTryAgain, onLoadMore, error]);

  return (
    <Table<Asset>
      style={tableStyle}
      data={assets}
      columns={columns}
      hasStickyHeader
      hasVirtualList
      isMultiSelectable={isMultiSelectable}
      isSingleSelectable={!isMultiSelectable}
      setTableOptions={(opts) => ({
        ...opts,
        getRowId,
        defaultColumn,
        autoResetSelectedRows: false,
      })}
      selectedRowIds={selectedRowIds}
      disabledRowIds={disabledRowIds}
      onAction={onAction}
      renderLastRow={renderLastRow}
    />
  );
};

AssetsCustomDataView.displayName = 'AssetsCustomDataView';

export default AssetsCustomDataView;
